/* Mission editor with live feasibility.
 *
 * The feasibility block is the point. Command PE gives you estimation only inside the
 * Operation Planner; here it updates as you change assignment or station count, so the
 * operator finds out a patrol is not sustainable while setting it up.
 */
import { AlertTriangle, CheckCircle2, Crosshair, Plane, Target, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { assessMission } from '../lib/feasibility'
import { hoursToHm } from '../lib/course'
import { MISSION_LABEL, type Mission } from '../data/missions'
import { nm } from '../lib/format'

function TypeIcon({ type }: { type: Mission['type'] }) {
  if (type === 'strike') return <Target size={12} aria-hidden />
  if (type === 'support') return <Plane size={12} aria-hidden />
  return <Crosshair size={12} aria-hidden />
}

export function MissionEditor() {
  const open = useDeck((s) => s.missionsOpen)
  const toggle = useDeck((s) => s.toggleMissions)
  const missions = useDeck((s) => s.missions)
  const entities = useDeck((s) => s.entities)
  const refPoints = useDeck((s) => s.refPoints)
  const selectedId = useDeck((s) => s.selectedMissionId)
  const selectMission = useDeck((s) => s.selectMission)
  const setOnStation = useDeck((s) => s.missionSetOnStation)
  const toggleAssign = useDeck((s) => s.missionToggleAssigned)
  const select = useDeck((s) => s.select)

  if (!open) return null

  const mission = missions.find((m) => m.id === selectedId) ?? missions[0]
  const f = mission ? assessMission(mission, entities, refPoints) : null

  return (
    <div className="panel absolute left-3 top-3 z-20 w-80 overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">Missions</span>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={toggle}
          aria-label="Close missions panel"
        >
          <X size={12} />
        </button>
      </div>

      {/* Mission list */}
      <div style={{ borderBottom: '1px solid var(--color-line-2)' }}>
        {missions.length === 0 && (
          // An empty panel with no explanation reads as a broken panel. It is not: this exercise
          // has no missions, and missions travel in the scenario document rather than being
          // authored here.
          <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
            This exercise carries no missions. Missions are part of the scenario document — the
            shipped Baltic exercise has six; a scenario authored in the ORBAT builder has none
            until they are added to its document.
          </p>
        )}
        {missions.map((m) => {
          const active = m.id === mission?.id
          return (
            <button
              key={m.id}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-[var(--color-surface-2)]"
              style={{ background: active ? 'var(--color-accent-dim)' : undefined }}
              onClick={() => selectMission(m.id)}
              aria-pressed={active}
            >
              <span style={{ color: active ? 'var(--color-accent-hi)' : 'var(--color-text-3)' }}>
                <TypeIcon type={m.type} />
              </span>
              <span
                className="num min-w-0 flex-1 truncate"
                style={{ fontSize: 11.5, color: active ? 'var(--color-text-1)' : 'var(--color-text-2)' }}
              >
                {m.name}
              </span>
              <span className="label shrink-0" style={{ fontSize: 8 }}>
                {MISSION_LABEL[m.type]}
              </span>
              <span className="num shrink-0" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                {m.assigned.length}
              </span>
            </button>
          )
        })}
      </div>

      {mission && f && (
        <>
          {/* Area */}
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>Area</span>
            <span className="num" style={{ fontSize: 10.5 }}>
              {mission.areaRefPoints.length
                ? `${mission.areaRefPoints.length} ref points`
                : '— none —'}
            </span>
          </div>

          {/* On-station requirement */}
          <div className="row">
            <span className="label" style={{ fontSize: 9 }}>On station</span>
            <div className="flex items-center gap-1">
              <button
                className="ctl"
                style={{ width: 18, height: 18, padding: 0 }}
                onClick={() => setOnStation(mission.id, Math.max(1, mission.onStation - 1))}
                aria-label="Decrease units on station"
              >
                −
              </button>
              <span className="num" style={{ fontSize: 12, minWidth: 14, textAlign: 'center' }}>
                {mission.onStation}
              </span>
              <button
                className="ctl"
                style={{ width: 18, height: 18, padding: 0 }}
                onClick={() => setOnStation(mission.id, mission.onStation + 1)}
                aria-label="Increase units on station"
              >
                +
              </button>
            </div>
          </div>

          {/* Assignment — per-unit transit and on-station time */}
          <div className="label px-3 pb-1 pt-2">Assigned</div>
          {f.units.map((u) => (
            <button
              key={u.entityId}
              className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
              style={{
                borderBottom: '1px solid var(--color-line-1)',
                background: u.viable ? undefined : 'rgba(232,81,90,0.08)',
              }}
              onClick={() => select({ kind: 'entity', id: u.entityId })}
              title="Select this unit"
            >
              <span className="num min-w-0 flex-1 truncate" style={{ fontSize: 11 }}>
                {u.designation}
              </span>
              <span className="num shrink-0" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
                {nm(u.transitNm, 0)}<span className="unit">nm</span>
              </span>
              <span
                className="num shrink-0"
                style={{
                  fontSize: 10.5,
                  minWidth: 52,
                  textAlign: 'right',
                  color: !u.viable
                    ? 'var(--color-crit)'
                    : u.onStationHours < 1
                      ? 'var(--color-warn)'
                      : 'var(--color-text-2)',
                }}
                title="Time on station after transit out and back"
              >
                {u.viable ? hoursToHm(u.onStationHours) : 'cannot'}
              </span>
            </button>
          ))}

          {/* Unassigned candidates on the same side */}
          {entities
            .filter((e) => e.side === mission.side && !mission.assigned.includes(e.id) && e.speedKt > 0)
            .map((e) => (
              <button
                key={e.id}
                className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                style={{ borderBottom: '1px solid var(--color-line-1)', opacity: 0.55 }}
                onClick={() => toggleAssign(mission.id, e.id)}
                title={`Assign ${e.designation} to ${mission.name}`}
              >
                <span className="num min-w-0 flex-1 truncate" style={{ fontSize: 11 }}>
                  {e.designation}
                </span>
                <span className="label shrink-0" style={{ fontSize: 8 }}>assign</span>
              </button>
            ))}

          {/* Feasibility — the reason this panel exists */}
          <div
            className="flex items-start gap-2 px-3 py-2"
            style={{
              borderTop: '1px solid var(--color-line-2)',
              background: f.canSustainRequested
                ? 'rgba(79,196,138,0.08)'
                : 'rgba(232,81,90,0.10)',
            }}
          >
            {f.canSustainRequested ? (
              <CheckCircle2 size={12} style={{ color: 'var(--color-ok)', marginTop: 1 }} aria-hidden />
            ) : (
              <AlertTriangle size={12} style={{ color: 'var(--color-crit)', marginTop: 1 }} aria-hidden />
            )}
            <div style={{ fontSize: 11, color: 'var(--color-text-2)', lineHeight: 1.45 }}>
              <div
                style={{
                  fontWeight: 500,
                  color: f.canSustainRequested ? 'var(--color-ok)' : 'var(--color-crit)',
                }}
              >
                {f.note}
              </div>
              {f.bestOnStationHours > 0 && (
                <div className="mt-0.5">
                  Fuel-limited at{' '}
                  <span className="num">{hoursToHm(f.bestOnStationHours)}</span> for a single unit.
                  {/* Rotation coverage only means something once the stations can
                      actually be filled. Below that it is an arithmetic artefact. */}
                  {f.canSustainRequested && (
                    <>
                      {' '}
                      <span className="num">
                        {hoursToHm(f.totalCoverageHours / Math.max(mission.onStation, 1))}
                      </span>{' '}
                      of coverage rotating {f.units.filter((u) => u.viable).length} units through{' '}
                      {mission.onStation} station{mission.onStation > 1 ? 's' : ''}.
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <p className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
            Transit-out / on-station / transit-back at current speed. Ignores climb, loiter
            speed, reserves and tanking.
          </p>
        </>
      )}
    </div>
  )
}
